import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Profile, DifficultyFilter, SkillFilter, RideTypeFilter, TimeFilter, MultiDayFilter, Point } from '@/lib/types/core';
import { TerrainType } from '@/lib/types/core/terrain';
import { groupRidesByTimeframe } from '@/lib/utils';
import { appsAdvApiRouterListRides, RideOut } from '@/lib/api';

export const useRideData = (
  difficultyFilter: DifficultyFilter,
  skillFilter: SkillFilter,
  rideTypeFilter: RideTypeFilter,
  timeFilter: TimeFilter,
  multiDayFilter: MultiDayFilter,
  terrainFilter: TerrainType[],
  nearbyPoint: Point | null,
  setNearbyPoint: (point: Point | null) => void,
  user: Profile | null,
  userLoading: boolean
) => {
  const [rides, setRides] = useState<RideOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    // Wait for user to finish loading before proceeding
    if (userLoading) {
      return;
    }

    let nearby = nearbyPoint
    if (!nearby && user && user.geo_location) {
      nearby = user.geo_location;
      setNearbyPoint(nearby);
    }
    const fetchRides = async () => {
      try {
        setLoading(true);

        const response = await appsAdvApiRouterListRides({
          difficulty: difficultyFilter !== 'All' ? difficultyFilter : undefined,
          skill: skillFilter !== 'All' ? skillFilter : undefined,
          ride_type: rideTypeFilter !== 'All' ? rideTypeFilter : undefined,
          time_filter: timeFilter,
          multi_day: multiDayFilter !== 'All' ? (multiDayFilter === 'Multi-day' ? 'true' : 'false') : undefined,
          terrain: terrainFilter.length > 0 ? terrainFilter.join(',') : undefined,
          nearby_latitude: nearby?.latitude,
          nearby_longitude: nearby?.longitude
        });

        setRides(response.data.results);
      } catch (error: any) {
        setRides([]);
        toast({
          title: "Error fetching rides",
          variant: "destructive"
        });
      } finally {
        setError(null);
        setMapError(null);
        setLoading(false);
      }
    };
    
    fetchRides();
  }, [difficultyFilter, skillFilter, rideTypeFilter, timeFilter, multiDayFilter, terrainFilter, toast, nearbyPoint, user, userLoading]);

  return {
    rides,
    loading,
    error,
    mapError
  };
};
